/**
 * @fileoverview Auto Scroll Controller Module.
 * Manages the auto-scrolling feature for a dynamic index section on the page.
 * This includes handling scroll behaviors, setting up titles and anchors, and controlling next/previous buttons.
 */

import { elementExists } from "../Utils";

const AutoScrollController = {
    dynamicIndexSelector: '.dynamic-index-js',
    nextButtonSelector: '.dynamic-index-next-js',
    prevButtonSelector: '.dynamic-index-prev-js',

    /**
     * Initializes the AutoScrollController.
     * Sets up titles, anchors, and scroll event listeners if the dynamic index element exists.
     */
    init() {
        if (!elementExists(this.dynamicIndexSelector)) return;

        this.setH2Titles();
        this.setAnchors();

        const dynamicIndex = document.querySelector(this.dynamicIndexSelector);
        const nextButton = document.querySelector(this.nextButtonSelector);
        const prevButton = document.querySelector(this.prevButtonSelector);

        nextButton.addEventListener('click', this.next.bind(this));
        prevButton.addEventListener('click', this.prev.bind(this));
        dynamicIndex.addEventListener('scroll', this.handleScroll.bind(this));
    },

    /**
     * Sets IDs for all h2 elements for anchor linking.
     */
    setH2Titles() {
        document.querySelectorAll('h2').forEach(h2 => {
            const content = h2.textContent;
            const id = this.sanitizeTitle(content);
            h2.setAttribute('id', id);
        });
    },

    /**
     * Sets up anchor elements for smooth scrolling to the target sections.
     */
    setAnchors() {
        document.querySelectorAll('.dynamic-index-anchor-js').forEach(anchor => {
            anchor.addEventListener('click', e => {
                e.preventDefault();
                const targetId = anchor.getAttribute('href');
                const targetElement = document.querySelector(targetId);
                let targetPosition = 0;
                if (window.pageYOffset === 0) {
                    targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset - 175;
                } else {
                    targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset - 125;
                    
                }
                

                window.scrollTo({
                    top: targetPosition,
                    behavior: 'smooth'
                });
            });
        });
    },

    /**
     * Sanitizes a title string to create a URL-friendly ID.
     * @param {string} title - The title to sanitize.
     * @returns {string} Sanitized string suitable for use as an ID.
     */
    sanitizeTitle(title) {
        return title.trim().toLowerCase().replace(/[^\w ]+/g, '').replace(/ +/g, '-');
    },

    /**
     * Scrolls the dynamic index container to the specified position.
     * @param {HTMLElement} dynamicIndex - The dynamic index container element.
     * @param {number} scrollPosition - The horizontal position to scroll to.
     */
    scroll(dynamicIndex, scrollPosition) {
        dynamicIndex.scrollTo({
            left: scrollPosition,
            top: 0,
            behavior: 'smooth'
        });
    },

    /**
     * Updates the state of the next and previous buttons based on the current scroll position.
     * @param {HTMLElement} dynamicIndex - The dynamic index container element.
     * @param {HTMLElement} nextButton - The next button element.
     * @param {HTMLElement} prevButton - The previous button element.
     */
    updateButtonState(dynamicIndex, nextButton, prevButton) {
        const dynamicIndexWidth = dynamicIndex.scrollWidth;
        const currentScrollPosition = dynamicIndex.scrollLeft;

        nextButton.classList.toggle('disabled', currentScrollPosition >= dynamicIndexWidth);
        prevButton.classList.toggle('disabled', currentScrollPosition <= 0);
    },

    /**
     * Event handler for the next button click. Scrolls to the next section.
     */
    next() {
        const dynamicIndex = document.querySelector(this.dynamicIndexSelector);
        const dynamicIndexWidth = dynamicIndex.scrollWidth - 10;
        const currentScrollPosition = dynamicIndex.scrollLeft;
        const scrollPosition = currentScrollPosition + dynamicIndex.clientWidth ; // Use clientWidth instead of scrollWidth
    
        this.scroll(dynamicIndex, scrollPosition);
    
        const nextButton = document.querySelector(this.nextButtonSelector);
        const prevButton = document.querySelector(this.prevButtonSelector);
    
        setTimeout(() => {
            if (scrollPosition + dynamicIndex.clientWidth >= dynamicIndexWidth) {
                nextButton.classList.add('disabled');
            }
            prevButton.classList.remove('disabled');
        }, 1000);

    },

    /**
     * Event handler for the previous button click. Scrolls to the previous section.
     */
    prev() {
        const dynamicIndex = document.querySelector(this.dynamicIndexSelector);
        const currentScrollPosition = dynamicIndex.scrollLeft;
        const scrollPosition = currentScrollPosition - dynamicIndex.clientWidth; // Use clientWidth instead of scrollWidth
    
        this.scroll(dynamicIndex, scrollPosition);
    
        const nextButton = document.querySelector(this.nextButtonSelector);
        const prevButton = document.querySelector(this.prevButtonSelector);
    
        if (scrollPosition <= 0) {
            prevButton.classList.add('disabled');
        }
        nextButton.classList.remove('disabled');
    },

    /**
     * Event handler for the scroll event on the dynamic index container.
     * Updates the state of the next and previous buttons.
     */
    handleScroll() {
        const dynamicIndex = document.querySelector(this.dynamicIndexSelector);
        const [nextButton, prevButton] = [this.nextButtonSelector, this.prevButtonSelector].map(selector =>
            document.querySelector(selector)
        );
        this.updateButtonState(dynamicIndex, nextButton, prevButton);
    }
};

export default AutoScrollController;